/**
 * OpenAPI Specification Parser
 *
 * Parses OpenAPI 3.0+ specifications (JSON or YAML) and extracts endpoint information.
 */

import { OpenAPISpec, ParsedEndpoint, EndpointParameter } from '../types';
import yaml from 'js-yaml';

/**
 * Parse OpenAPI specification and extract endpoints
 *
 * @param specString - OpenAPI specification as JSON or YAML string
 * @returns Array of parsed endpoints, sorted by path then method
 *
 * @example
 * ```typescript
 * const endpoints = parseOpenApiSpec(product.openapi_spec);
 * console.log(endpoints); // [{ id: 'GET:/users', method: 'GET', path: '/users', ... }]
 * ```
 */
export function parseOpenApiSpec(specString: string): ParsedEndpoint[] {
  const endpoints: ParsedEndpoint[] = [];

  try {
    // Step 1: Parse spec string (JSON or YAML)
    let spec: OpenAPISpec;

    if (specString.trim().startsWith('{')) {
      // Parse as JSON
      spec = JSON.parse(specString);
    } else {
      // Parse as YAML
      spec = yaml.load(specString) as OpenAPISpec;
    }

    // Step 2: Validate spec structure
    if (!spec || !spec.paths) {
      return [];
    }

    // Step 3: Extract endpoints from paths
    for (const path of Object.keys(spec.paths)) {
      const pathItem = spec.paths[path];

      // Step 4: Extract operations for each HTTP method
      const methods = ['get', 'post', 'put', 'delete', 'patch'];

      for (const method of methods) {
        if (pathItem[method]) {
          const operation = pathItem[method];

          // Step 5: Build endpoint object
          const endpoint: ParsedEndpoint = {
            id: `${method.toUpperCase()}:${path}`,
            method: method.toUpperCase() as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
            path: path,
            summary: operation.summary,
            description: operation.description,
            operationId: operation.operationId,
            parameters: extractParameters(operation.parameters || []),
            requestBodySchema: operation.requestBody?.content?.['application/json']?.schema,
            requiresBody: operation.requestBody?.required === true,
          };

          endpoints.push(endpoint);
        }
      }
    }

    // Step 6: Sort endpoints by path and method
    endpoints.sort((a, b) => {
      if (a.path !== b.path) {
        return a.path.localeCompare(b.path);
      }
      return a.method.localeCompare(b.method);
    });

    return endpoints;
  } catch (error) {
    // Graceful degradation: return empty array on any error
    console.error('Failed to parse OpenAPI spec:', error);
    return [];
  }
}

/**
 * Extract parameters from OpenAPI operation
 *
 * @param parameters - Array of parameter objects from OpenAPI spec
 * @returns Array of parsed endpoint parameters
 */
function extractParameters(parameters: any[]): EndpointParameter[] {
  return parameters.map((param) => ({
    name: param.name,
    in: param.in,
    required: param.required === true,
    type: param.schema?.type || 'string',
    description: param.description,
  }));
}
